import { initReactI18next } from 'react-i18next';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import {
  getCanonicalPathForHost,
  getDefaultLocalePath,
  getForcedLocaleForHost,
} from './utils/hostRouting';

const forcedLocaleForHost = getForcedLocaleForHost();

// Before using translations, check what is the parameter in the URL
i18next.init(() => {
  const canonicalPath = getCanonicalPathForHost(window.location.pathname);

  if (canonicalPath !== window.location.pathname) {
    window.location.href = `${window.location.origin}${canonicalPath}${window.location.search}${window.location.hash}`;
    return;
  }

  if (!forcedLocaleForHost && (window.location.pathname === '/' || window.location.pathname === '/undefined'))
    return (window.location.pathname = getDefaultLocalePath(undefined, i18next.language || navigator.language));

  if (forcedLocaleForHost) return;

  // If there is no parameter in the URL use what has been written in i18next.langauge, if nothing has been written then use the language of the browser 
  const pathLang = window.location.pathname.split('/')[1];
  const url = window.location.pathname.split('/');
  const langMap = {
    es: '/hazteoir',
    en: '/en-us',
    pt: '/pt-pt',
    fr: "/fr-fr",
  };
if(url.length === 2) {
  if (window.location.pathname === '/en')
    return (window.location.pathname = 'en-row');
  else if ((window.location.pathname === '/es') || (window.location.pathname === '/es-ho'))
    return (window.location.pathname = 'hazteoir');
  else if (window.location.pathname === '/pt')
    return (window.location.pathname = 'pt-pt');
  else if (window.location.pathname === '/fr')
    return (window.location.pathname = 'fr-fr');
  else if (window.location.pathname === '/unsubscription')
    return ;
  else if (!i18next.options.supportedLngs.includes(pathLang)) {
    window.location.href = `${langMap[pathLang]}${window.location.search}` || '/en-row/notfound';
  }
}
});

i18next
  // initialize i18next
  .use(initReactI18next)
  // detect user language
  .use(LanguageDetector)
  // load translation using http -> see /public/locales
  .use(HttpApi)
  // pass the i18n instance to react-i18next
  .init({
    ...(forcedLocaleForHost ? { lng: forcedLocaleForHost } : {}),
    supportedLngs: [
      'pl',
      'pl-pl',
      'hazteoir',
      'es-ho',
      'es-mx',
      'fr-fr',
      'fr-ca',
      'hr',
      'hr-hr',
      'it',
      'it-it',
      'en-us',
      'en-af',
      'en-ca',
      'en-au',
      'en-row',
      'en-gb',
      'en-ie',
      'es-lat',
      'es-ar',
      'hu',
      'hu-hu',
      'nl',
      'nl-be',
      'nl-nl',
      'pt-br',
      'pt-pt',
      'sk',
      'sk-sk',
      'de',
      'de-de',
      'de-at',
      'de-ch',
      'es',
      'pt',
      'en',
      'es-us',
      'fr',
      'fr-fr',
      'vls-be',
      'es-row'
    ],
    nonExplicitSupportedLngs: false,
    cleanCode: true,
    fallbackLng: {
      'pl-pl': ['pl', 'en-row'],
      'hr-hr': ['hr', 'en-row'],
      'it-it': ['it', 'en-row'],
      'hu-hu': ['hu', 'en-row'],
      'nl-nl': ['nl', 'en-row'],
      'sk-sk': ['sk', 'en-row'],
      'de-de': ['de', 'en-row'],
      'vls-be': ['nl-be', 'en-row'],
      'es-row': ['es-lat', 'en-row'],
      'es-ho': ['hazteoir'],
      'hu': ['en-row'],
      'pl': ['en-row'],
      default: ['en-row']
    },
    lowerCaseLng: true,
    detection: {
      // order and from where user language should be detected
      order: [
        'path',
        'cookie',
        'navigator',
        'localStorage',
        'subdomain',
        'htmlTag',
      ],
      // keys or params to lookup language from
      caches: ['cookie'],
    },
    // dir to load translation files
    backend: {
      loadPath: '/assets/locales/{{lng}}/translation.json',
    },
    react: { useSuspense: false },
    initImmediate: false,
    debug: false,
  });

// Reduce an internal locale code (e.g. 'pl-pl', 'en-row', 'hazteoir') to a
// valid HTML `lang` value (primary language subtag). This keeps the value
// meaningful to browser translation engines: 'hazteoir' is a CitizenGO brand
// code for Spanish, and codes such as 'en-row' carry a non-standard region
// subtag whose primary subtag ('en') is what matters.
export const getHtmlLang = (lng) => {
  if (!lng) return 'en';
  const normalized = String(lng).toLowerCase();
  if (normalized === 'hazteoir') return 'es';
  return normalized.split('-')[0];
};

// Keep <html lang> in sync with the active locale so browser translation
// engines (e.g. Chrome/Google Translate) detect the real page language and do
// not mistranslate it (e.g. treating a Polish petition page as English and
// auto-translating it into garbled Polish).
const syncDocumentLang = (lng) => {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = getHtmlLang(lng);
  }
};

i18next.on('languageChanged', syncDocumentLang);
// Apply the initial locale detected during init, which may have fired before
// the listener above was registered.
syncDocumentLang(i18next.language);

export default i18next;
